// Shared session state for the local user (self).
//
// Holds the bits the UI needs to read in two or more places:
//
//   - identity (name, tag, accountXid)
//   - presence status (online / away / busy / offline / invisible)
//   - mic-muted toggle
//   - notifications-deafened toggle
//   - whether the profile modal is currently open
//
// `online`, `away`, `busy`, `offline` mirror the placeholder
// `FriendStatus` shape; `invisible` is a UI-only choice that
// presents as `offline` to other clients but still lets the local
// user behave normally.

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

import { BADGES, type BadgeId } from "../lib/badges";
import { api, type CommunityTagSummary } from "../lib/api";
import { readSessionToken } from "../lib/http";
import { updateDeviceAccountProfile } from "../lib/device-vault";

export type SelfStatus =
  | "online"
  | "away"
  | "busy"
  | "invisible"
  | "offline";

export interface SelfIdentity {
  name: string;
  username: string;
  usernameChangeAvailableAt: string | null;
  tag: string;
  accountXid: string;
  /** Optional avatar URL; falls back to initials. */
  avatarUrl: string | null;
  bannerUrl?: string | null;
  bannerColor: string;
  bannerMode: "solid" | "gradient";
  bannerSecondaryColor: string;
  communityTag: CommunityTagSummary | null;
  /** "Custom status" tagline pinned next to the name. */
  customStatus: string | null;
  /** Optional public pronouns, for example "she/her". */
  pronouns: string | null;
  /** ISO date when this account was created. */
  memberSince: string;
  /** Local-only note the user keeps about themselves. */
  selfNote: string;
  /** Awarded badges (Link Staff, Early User, etc.). */
  badges?: ReadonlyArray<BadgeId>;
}

interface SelfSessionValue {
  identity: SelfIdentity;
  status: SelfStatus;
  micMuted: boolean;
  deafened: boolean;
  profileOpen: boolean;
  settingsOpen: boolean;
  /**
   * Section the SettingsModal should open to next time it's
   * mounted. Cleared by the modal once it consumes it.
   */
  pendingSettingsSection: string | null;

  setStatus: (status: SelfStatus) => void;
  toggleMic: () => void;
  toggleDeafen: () => void;
  setProfileOpen: (open: boolean) => void;
  setSettingsOpen: (open: boolean) => void;
  /** Open Settings deep-linked to a specific section. */
  openSettings: (section?: string) => void;
  /** Called by the SettingsModal once it consumes the deep link. */
  consumePendingSettingsSection: () => void;
  patchIdentity: (patch: Partial<SelfIdentity>) => void;
  saveProfile: (patch: Partial<SelfIdentity>) => Promise<void>;
  setCommunityTag: (tagXid: string | null) => Promise<void>;
  changeUsername: (username: string) => Promise<void>;
  uploadAvatar: (file: File) => Promise<void>;
  removeAvatar: () => Promise<void>;
}

const SelfSessionContext = createContext<SelfSessionValue | null>(null);

interface ProviderProps {
  initial: SelfIdentity;
  children: ReactNode;
}

export function SelfSessionProvider({ initial, children }: ProviderProps) {
  const [identity, setIdentity] = useState<SelfIdentity>(initial);
  const [status, setStatusState] = useState<SelfStatus>("online");
  const [micMuted, setMicMuted] = useState(false);
  const [deafened, setDeafened] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [pendingSettingsSection, setPendingSettingsSection] =
    useState<string | null>(null);

  useEffect(() => {
    if (!identity.accountXid || readSessionToken() === null) return;
    void updateDeviceAccountProfile(identity.accountXid, {
      username: identity.username, displayName: identity.name, avatarUrl: identity.avatarUrl,
    }).catch(() => undefined); // Optional presentation cache, never a sign-in requirement.
  }, [identity.accountXid, identity.username, identity.name, identity.avatarUrl]);

  useEffect(() => {
    if (readSessionToken() === null) return;
    let cancelled = false;
    void api.me
      .identity()
      .then((profile) => {
        if (cancelled) return;
        const badges = (profile.badges ?? []).filter(
          (badge): badge is BadgeId => badge in BADGES,
        );
        setIdentity((current) => ({
          ...current,
          name: profile.display_name,
          username: profile.username,
          usernameChangeAvailableAt: profile.username_change_available_at,
          tag: profile.username,
          accountXid: profile.account_xid,
          avatarUrl: profile.avatar_url,
          bannerUrl: profile.banner_url,
          bannerColor: profile.banner_color,
          bannerMode: profile.banner_mode,
          bannerSecondaryColor: profile.banner_secondary_color,
          communityTag: profile.community_tag,
          customStatus: profile.custom_status,
          pronouns: profile.pronouns,
          memberSince: profile.member_since,
          badges,
        }));
        setStatusState(profile.presence_status);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const onRealtimeEvent = (event: Event): void => {
      const frame = (
        event as CustomEvent<{
          type?: unknown;
          payload?: { account_xid?: unknown; badges?: unknown };
        }>
      ).detail;
      if (
        frame?.type === "community_tag.updated" &&
        frame.payload?.account_xid === identity.accountXid
      ) {
        void api.me.identity().then((profile) => {
          setIdentity((current) => ({ ...current, communityTag: profile.community_tag }));
        }).catch(() => undefined);
        return;
      }
      if (
        frame?.type !== "profile.updated" ||
        frame.payload?.account_xid !== identity.accountXid ||
        !Array.isArray(frame.payload.badges)
      ) {
        return;
      }
      const badges = frame.payload.badges.filter(
        (badge): badge is BadgeId =>
          typeof badge === "string" && badge in BADGES,
      );
      setIdentity((current) => ({ ...current, badges }));
    };
    window.addEventListener("link:realtime-event", onRealtimeEvent);
    return () => window.removeEventListener("link:realtime-event", onRealtimeEvent);
  }, [identity.accountXid]);

  const toggleMic = useCallback(() => {
    setMicMuted((prev) => !prev);
  }, []);

  const toggleDeafen = useCallback(() => {
    setDeafened((prev) => {
      const next = !prev;
      // Deafen implies mic-mute on most chat apps; we mirror that
      // ergonomic so users only have to flip one switch in noisy
      // contexts.
      if (next) setMicMuted(true);
      return next;
    });
  }, []);

  const patchIdentity = useCallback((patch: Partial<SelfIdentity>) => {
    setIdentity((prev) => ({ ...prev, ...patch }));
    if (readSessionToken() !== null) {
      const body = {
        ...(patch.name !== undefined ? { display_name: patch.name } : {}),
        ...(patch.bannerUrl !== undefined ? { banner_url: patch.bannerUrl } : {}),
        ...(patch.bannerColor !== undefined ? { banner_color: patch.bannerColor } : {}),
        ...(patch.bannerMode !== undefined ? { banner_mode: patch.bannerMode } : {}),
        ...(patch.bannerSecondaryColor !== undefined ? { banner_secondary_color: patch.bannerSecondaryColor } : {}),
        ...(patch.customStatus !== undefined
          ? { custom_status: patch.customStatus }
          : {}),
        ...(patch.pronouns !== undefined ? { pronouns: patch.pronouns } : {}),
      };
      if (Object.keys(body).length > 0) {
        void api.me.patch(body).catch(() => undefined);
      }
    }
  }, []);

  const applyProfile = useCallback((profile: Awaited<ReturnType<typeof api.me.identity>>) => {
    const badges = (profile.badges ?? []).filter(
      (badge): badge is BadgeId => badge in BADGES,
    );
    setIdentity((current) => ({
      ...current,
      name: profile.display_name,
      username: profile.username,
      usernameChangeAvailableAt: profile.username_change_available_at,
      tag: profile.username,
      accountXid: profile.account_xid,
      avatarUrl: profile.avatar_url,
      bannerUrl: profile.banner_url,
      bannerColor: profile.banner_color,
      bannerMode: profile.banner_mode,
      bannerSecondaryColor: profile.banner_secondary_color,
      communityTag: profile.community_tag,
      customStatus: profile.custom_status,
      pronouns: profile.pronouns,
      memberSince: profile.member_since,
      badges,
    }));
    setStatusState(profile.presence_status);
  }, []);

  const saveProfile = useCallback(async (patch: Partial<SelfIdentity>) => {
    const body = {
      ...(patch.name !== undefined ? { display_name: patch.name } : {}),
      ...(patch.bannerUrl !== undefined ? { banner_url: patch.bannerUrl } : {}),
      ...(patch.bannerColor !== undefined ? { banner_color: patch.bannerColor } : {}),
      ...(patch.bannerMode !== undefined ? { banner_mode: patch.bannerMode } : {}),
      ...(patch.bannerSecondaryColor !== undefined ? { banner_secondary_color: patch.bannerSecondaryColor } : {}),
      ...(patch.customStatus !== undefined ? { custom_status: patch.customStatus } : {}),
      ...(patch.pronouns !== undefined ? { pronouns: patch.pronouns } : {}),
    };
    if (Object.keys(body).length > 0) applyProfile(await api.me.patch(body));
    if (patch.selfNote !== undefined) {
      setIdentity((current) => ({ ...current, selfNote: patch.selfNote ?? "" }));
    }
  }, [applyProfile]);

  const setCommunityTag = useCallback(async (tagXid: string | null) => {
    if (tagXid === null) await api.communityTags.unequip();
    else await api.communityTags.equip(tagXid);
    applyProfile(await api.me.identity());
  }, [applyProfile]);

  const changeUsername = useCallback(async (username: string) => {
    applyProfile(await api.me.patch({ username }));
  }, [applyProfile]);

  const uploadAvatar = useCallback(async (file: File) => {
    const result = await api.me.uploadAvatar(file);
    setIdentity((current) => ({ ...current, avatarUrl: result.avatar_url }));
  }, []);

  const removeAvatar = useCallback(async () => {
    await api.me.removeAvatar();
    setIdentity((current) => ({ ...current, avatarUrl: null }));
  }, []);

  const setStatus = useCallback((next: SelfStatus) => {
    setStatusState(next);
    if (readSessionToken() !== null) {
      void api.me.setPresence(next).catch(() => {
        void api.me.identity().then((profile) => {
          setStatusState(profile.presence_status);
        }).catch(() => undefined);
      });
    }
  }, []);

  const openSettings = useCallback((section?: string) => {
    if (section !== undefined) setPendingSettingsSection(section);
    setSettingsOpen(true);
  }, []);

  const consumePendingSettingsSection = useCallback(() => {
    setPendingSettingsSection(null);
  }, []);

  const value = useMemo<SelfSessionValue>(
    () => ({
      identity,
      status,
      micMuted,
      deafened,
      profileOpen,
      settingsOpen,
      pendingSettingsSection,
      setStatus,
      toggleMic,
      toggleDeafen,
      setProfileOpen,
      setSettingsOpen,
      openSettings,
      consumePendingSettingsSection,
      patchIdentity,
      saveProfile,
      setCommunityTag,
      changeUsername,
      uploadAvatar,
      removeAvatar,
    }),
    [
      identity,
      status,
      micMuted,
      deafened,
      profileOpen,
      settingsOpen,
      pendingSettingsSection,
      setStatus,
      toggleMic,
      toggleDeafen,
      openSettings,
      consumePendingSettingsSection,
      patchIdentity,
      saveProfile,
      setCommunityTag,
      changeUsername,
      uploadAvatar,
      removeAvatar,
    ],
  );

  return (
    <SelfSessionContext.Provider value={value}>
      {children}
    </SelfSessionContext.Provider>
  );
}

export function useSelf(): SelfSessionValue {
  const ctx = useContext(SelfSessionContext);
  if (ctx === null) {
    throw new Error("useSelf must be called inside <SelfSessionProvider>");
  }
  return ctx;
}

/**
 * Visible status mapped to the FriendStatus dot color. `invisible`
 * presents as `offline` to peers; the SelfSession context keeps the
 * underlying value so the local UI can show "Invisible" in the
 * status picker.
 */
export function visibleStatus(status: SelfStatus): "online" | "away" | "busy" | "offline" {
  return status === "invisible" ? "offline" : status;
}
